import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MODAL_WIDTH } from '../const';
import { useUIStore } from '../stores/ui/uiStore';
import useEscapeKeyClose from '../hooks/useEscapeKeyClose';
import { useDataStore } from '../stores/data/dataStore';
import TokenDetailHeader from './TokenDetailHeader';
import GmgnChart from './GmgnChart';
import {
  feManagementSystemApi,
  MonitoringResultDto,
  SocialSyncTargetDto,
} from '../api/fe-management-system-api';
import { api, Author, Tweet, TweetSyncResponse } from '../api/af-social-tracker-api';
import TweetCard, { Tweet as TweetCardTweet } from './TweetCard';
import { formatTimestampToLocaleString } from '../utils/time';
import SocialSourceTypeChip from './SocialSourceTypeChip';
import CopyButton from './CopyButton';
import { useToasterStore } from '../stores/ui/toasterStore';
import SocialSourceDetailPanel, { type SocialDetailRowFocus } from './SocialSourceDetailPanel';

interface SocialDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SOCIAL_MODAL_WIDTH = Math.round(MODAL_WIDTH * 1.3);
const TIMELINE_PAGE_DAYS = 2;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

type SourceCardDetail = {
  isLoading: boolean;
  tweet: TweetCardTweet | null;
  profile: Author | null;
  mode: 'tweet' | 'account';
  message?: string;
};

type SocialSourceRow = {
  sourceType: 'X_TWEET' | 'X_ACCOUNT' | 'X_COMMUNITY' | string;
  sourceKey: string;
  from?: Date | string;
  to?: Date | string;
};

type SyncResultRecord = {
  state?: string;
  monitoring_since?: string | Date;
  monitoring_until?: string | Date;
  monitoringSince?: string | Date;
  monitoringUntil?: string | Date;
  [key: string]: unknown;
};

type SocialSourceCardModel = {
  cardId: string;
  sourceType: string;
  sourceKey: string;
  from?: Date | string;
  to?: Date | string;
  trackerStatus?: string;
  origin: 'note' | 'sync';
  syncTargetId?: string;
  enabled?: boolean;
  accountPinnedTweetId?: string;
  syncResults?: SyncResultRecord[];
  tweetSyncId?: string;
  isLegacyTweetSyncFallback?: boolean;
};

const LEGACY_COMMUNITY_FALLBACK_KEY = 'LEGACY_COMMUNITY';

const buildLegacyCommunityFallbackCard = (
  tweetSync: TweetSyncResponse,
  existingCards: SocialSourceCardModel[],
): SocialSourceCardModel | null => {
  const hasCommunityCard = existingCards.some(
    (card) => card.sourceType?.toUpperCase() === 'X_COMMUNITY',
  );
  if (hasCommunityCard) {
    return null;
  }

  const normalizedCommunityId = (tweetSync.communityId ?? '').toString().trim();
  return {
    cardId: `legacy-community:${tweetSync._id}`,
    sourceType: 'X_COMMUNITY',
    sourceKey: normalizedCommunityId || LEGACY_COMMUNITY_FALLBACK_KEY,
    origin: 'sync',
    enabled: true,
    trackerStatus: tweetSync.status,
    tweetSyncId: tweetSync._id,
    isLegacyTweetSyncFallback: true,
  };
};

const normalizeSyncTargetDto = (raw: SocialSyncTargetDto) => ({
  id: raw._id,
  sourceType: (raw.source_type ?? raw.sourceType ?? 'UNKNOWN').toString().toUpperCase(),
  sourceKey: (raw.source_key ?? raw.sourceKey ?? '').toString(),
  enabled: raw.enabled !== false,
  state: raw.state,
  results: raw.results,
});

const getRangeFromSyncedRanges = (
  state: unknown,
): { from?: Date | string; to?: Date | string } => {
  const stateObj = (state ?? {}) as Record<string, unknown>;
  const syncedRangesRaw =
    stateObj.synced_ranges ?? stateObj.syncedRanges ?? [];

  if (!Array.isArray(syncedRangesRaw) || syncedRangesRaw.length === 0) {
    return {};
  }

  const syncedRanges = syncedRangesRaw
    .map((range) => range as Record<string, unknown>)
    .map((range) => ({
      since:
        (range.since_time ??
          range.sinceTime ??
          range.from) as Date | string | undefined,
      until:
        (range.until_time ??
          range.untilTime ??
          range.to) as Date | string | undefined,
    }))
    .filter((range) => range.since && range.until);

  if (syncedRanges.length === 0) {
    return {};
  }

  const toMs = (value: Date | string | undefined): number =>
    value instanceof Date ? value.getTime() : new Date(value ?? '').getTime();

  const from = syncedRanges.reduce((min, curr) =>
    toMs(curr.since) < toMs(min.since) ? curr : min,
  ).since;
  const to = syncedRanges.reduce((max, curr) =>
    toMs(curr.until) > toMs(max.until) ? curr : max,
  ).until;

  return {
    from: from ?? undefined,
    to: to ?? undefined,
  };
};

const getRangeFromLatestResult = (
  results: unknown,
): { from?: Date | string; to?: Date | string } => {
  if (!Array.isArray(results) || results.length === 0) {
    return {};
  }

  const normalized = results
    .map((item) => item as Record<string, unknown>)
    .map((item, index) => {
      const createdAtRaw = item.created_at ?? item.createdAt ?? item.updated_at ?? item.updatedAt;
      const createdAtMs =
        createdAtRaw instanceof Date
          ? createdAtRaw.getTime()
          : new Date((createdAtRaw as string | number | undefined) ?? '').getTime();
      return {
        from:
          (item.monitoring_since ??
            item.monitoringSince ??
            item.from) as Date | string | undefined,
        to:
          (item.monitoring_until ??
            item.monitoringUntil ??
            item.to) as Date | string | undefined,
        createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : -1,
        index,
      };
    })
    .filter((item) => item.from && item.to);

  if (normalized.length === 0) {
    return {};
  }

  normalized.sort((a, b) => {
    if (a.createdAtMs !== b.createdAtMs) {
      return b.createdAtMs - a.createdAtMs;
    }
    return b.index - a.index;
  });

  return {
    from: normalized[0].from ?? undefined,
    to: normalized[0].to ?? undefined,
  };
};

const getLatestResultRecord = (results: unknown): Record<string, unknown> | null => {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  const normalized = results
    .map((item) => item as Record<string, unknown>)
    .map((item, index) => {
      const createdAtRaw = item.created_at ?? item.createdAt ?? item.updated_at ?? item.updatedAt;
      const createdAtMs =
        createdAtRaw instanceof Date
          ? createdAtRaw.getTime()
          : new Date((createdAtRaw as string | number | undefined) ?? '').getTime();
      return {
        item,
        createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : -1,
        index,
      };
    });

  normalized.sort((a, b) => {
    if (a.createdAtMs !== b.createdAtMs) {
      return b.createdAtMs - a.createdAtMs;
    }
    return b.index - a.index;
  });

  return normalized[0]?.item ?? null;
};

const buildSourceCardsFromNoteRows = (rows: SocialSourceRow[]): SocialSourceCardModel[] =>
  rows.map((row, index) => ({
    cardId: `note:${index}`,
    sourceType: row.sourceType,
    sourceKey: row.sourceKey,
    from: row.from,
    to: row.to,
    origin: 'note' as const,
  }));

const buildSourceCardsFromSyncTargets = (
  targets: SocialSyncTargetDto[],
  noteRows: SocialSourceRow[],
  trackerStatuses: Map<string, string>,
): SocialSourceCardModel[] =>
  targets
    .map((raw) => normalizeSyncTargetDto(raw))
    .filter((t) => t.sourceKey)
    .map((t) => {
      const noteMatch = noteRows.find(
        (n) =>
          n.sourceKey === t.sourceKey &&
          n.sourceType?.toUpperCase() === t.sourceType?.toUpperCase(),
      );
      const syncedRange = getRangeFromSyncedRanges(t.state);
      const latestResult = getLatestResultRecord(
        t.results ?? ((t.state as Record<string, unknown> | undefined)?.results ?? []),
      );
      const latestResultRange = getRangeFromLatestResult(
        t.results ?? ((t.state as Record<string, unknown> | undefined)?.results ?? []),
      );
      const accountPayload = (latestResult?.account ?? {}) as Record<string, unknown>;
      const accountPinnedTweetId =
        (accountPayload.pinned_tweet_id ?? accountPayload.pinnedTweetId) as
          | string
          | undefined;
      const rawResults = (t.results ?? []) as unknown[];
      const syncResults: SyncResultRecord[] = rawResults
        .filter((r): r is Record<string, unknown> => r != null && typeof r === 'object')
        .map((r) => r as SyncResultRecord);
      return {
        cardId: `sync:${t.id}`,
        sourceType: t.sourceType,
        sourceKey: t.sourceKey,
        from: syncedRange.from ?? latestResultRange.from ?? noteMatch?.from,
        to: syncedRange.to ?? latestResultRange.to ?? noteMatch?.to,
        trackerStatus:
          trackerStatuses.get(
            `${t.sourceType.toUpperCase()}::${t.sourceKey.toLowerCase()}`,
          ) ?? undefined,
        origin: 'sync' as const,
        syncTargetId: t.id,
        enabled: t.enabled,
        accountPinnedTweetId,
        syncResults,
      };
    });

const getStatusChipClassName = (status?: string): string => {
  switch ((status ?? '').toUpperCase()) {
    case 'SYNCING':
      return 'border-amber-500/40 bg-amber-500/10 text-amber-300';
    case 'COMPLETED':
      return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300';
    case 'STOPPED':
      return 'border-teal-500/40 bg-teal-500/10 text-teal-300';
    case 'ERROR':
    case 'FAILED':
      return 'border-rose-500/40 bg-rose-500/10 text-rose-300';
    case 'ACTIVE':
      return 'border-sky-500/40 bg-sky-500/10 text-sky-300';
    case 'PENDING':
      return 'border-gray-500/40 bg-gray-500/10 text-gray-300';
    default:
      return 'border-gray-600 bg-gray-700/30 text-gray-300';
  }
};

const getSyncEnabledChipClassName = (enabled: boolean): string =>
  enabled
    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
    : 'border-gray-600 bg-gray-700/40 text-gray-400';

/** Opens the corresponding page on X (twitter.com) in a new tab. */
const getXWebUrlForSocialSource = (
  sourceType: string,
  sourceKey: string,
): string | null => {
  const key = sourceKey.trim();
  if (!key) {
    return null;
  }
  switch (sourceType.toUpperCase()) {
    case 'X_TWEET':
      return `https://x.com/i/status/${encodeURIComponent(key)}`;
    case 'X_COMMUNITY':
      if (!/^\d+$/.test(key)) {
        return null;
      }
      return `https://x.com/i/communities/${encodeURIComponent(key)}`;
    case 'X_ACCOUNT': {
      const handle = key.replace(/^@+/, '').trim();
      if (!handle) {
        return null;
      }
      return `https://x.com/${encodeURIComponent(handle)}`;
    }
    default:
      return null;
  }
};

const mapApiTweetToCardTweet = (tweet: Tweet): TweetCardTweet => ({
  id: tweet.id,
  text: tweet.text,
  createdAt: tweet.createdAt,
  author: {
    id: tweet.author.id,
    userName: tweet.author.userName,
    name: tweet.author.name ?? undefined,
    profilePicture: tweet.author.profilePicture ?? undefined,
    followers: tweet.author.followers ?? undefined,
    label: tweet.author.label ?? undefined,
  },
  viewCount: tweet.viewCount,
  likeCount: tweet.likeCount,
  bookmarkCount: tweet.bookmarkCount,
  retweetCount: tweet.retweetCount,
  quoteCount: tweet.quoteCount,
  replyCount: tweet.replyCount,
});

const getDisplayHandle = (userName?: string): string => {
  const normalized = (userName ?? '').trim();
  return normalized.length > 0 ? normalized : 'unknown';
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('timeout')), timeoutMs);
    });
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const SocialDetailModal: React.FC<SocialDetailModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { selectedToken, openCreateNoteModal } = useUIStore();
  const tokenAddress = selectedToken?.tokenAddress ?? null;
  const [monitoringResults, setMonitoringResults] = useState<MonitoringResultDto[]>([]);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);
  const [visibleTimelineDays, setVisibleTimelineDays] = useState(TIMELINE_PAGE_DAYS);
  const [pendingScrollRowIndex, setPendingScrollRowIndex] = useState<number | null>(null);
  const [sourceCards, setSourceCards] = useState<SocialSourceCardModel[]>([]);
  const [selectedSourceCardId, setSelectedSourceCardId] = useState<string | null>(null);
  const [sourceCardDetails, setSourceCardDetails] = useState<Record<string, SourceCardDetail>>(
    {},
  );
  const [isLegacyTweetSyncMode, setIsLegacyTweetSyncMode] = useState(false);
  const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
  const [loadedNoteContents, setLoadedNoteContents] = useState('');
  const [newSourceType, setNewSourceType] = useState<'X_TWEET' | 'X_ACCOUNT' | 'X_COMMUNITY'>('X_TWEET');
  const [newSourceKey, setNewSourceKey] = useState('');
  const [isCreatingSource, setIsCreatingSource] = useState(false);
  const [syncTargetToggleBusyId, setSyncTargetToggleBusyId] = useState<string | null>(null);
  const [syncResultViewFocus, setSyncResultViewFocus] = useState<SocialDetailRowFocus>(null);
  const syncResultFocusSeqRef = useRef(0);
  const { showToaster } = useToasterStore();
  const timelineSectionRef = useRef<HTMLElement | null>(null);
  const timelineRowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const tokenData = useDataStore((state) =>
    tokenAddress ? state.tokenStateMap[tokenAddress] : null,
  );
  const fetchAndAddTokenState = useDataStore((state) => state.fetchAndAddTokenState);

  const loadMonitoringLogs = useCallback(
    async (activeRef?: { current: boolean }) => {
      if (!tokenAddress) {
        setMonitoringResults([]);
        setSourceCards([]);
        setSyncResultViewFocus(null);
        setSelectedSourceCardId(null);
        setIsLegacyTweetSyncMode(false);
        return;
      }

      setIsTimelineLoading(true);
      setLoadedNoteContents('');
      try {
        const [targetsResult, tokenStateResult, statusesResult, tweetSyncResult] =
          await Promise.allSettled([
          feManagementSystemApi.listSocialSyncTargets(tokenAddress),
          feManagementSystemApi.getTokenState(tokenAddress),
          api.getTweetSyncSourceStatuses(tokenAddress),
          api.getTweetSync(tokenAddress),
          ]);

        if (activeRef && !activeRef.current) {
          return;
        }

        if (targetsResult.status === 'rejected') {
          console.error('Social sync targets 조회 실패:', targetsResult.reason);
        }
        if (statusesResult.status === 'rejected') {
          console.error('Social sync statuses 조회 실패:', statusesResult.reason);
        }

        const syncTargets: SocialSyncTargetDto[] =
          targetsResult.status === 'fulfilled' ? targetsResult.value : [];
        const nextLegacyMode =
          syncTargets.length === 0 && tweetSyncResult.status === 'fulfilled';
        setIsLegacyTweetSyncMode(nextLegacyMode);
        if (nextLegacyMode) {
          setIsAddSourceOpen(false);
        }
        const statusMap = new Map<string, string>(
          (statusesResult.status === 'fulfilled' ? statusesResult.value : []).map(
            (item) => [
              `${(item.sourceType ?? '').toUpperCase()}::${(item.sourceKey ?? '').toLowerCase()}`,
              item.status,
            ],
          ),
        );

        if (tokenStateResult.status === 'rejected') {
          throw tokenStateResult.reason;
        }

        const tokenState = tokenStateResult.value;
        setMonitoringResults(tokenState.monitoringResults ?? []);

        const note = tokenState.note;
        setLoadedNoteContents((note?.contents ?? '').toString());
        const rawSources = note?.socialSources ?? note?.social_sources ?? [];
        const normalizedNoteRows: SocialSourceRow[] = rawSources
          .map((source: any) => ({
            sourceType: (
              source?.sourceType ??
              source?.source_type ??
              'UNKNOWN'
            ).toString().toUpperCase(),
            sourceKey: (source?.sourceKey ?? source?.source_key ?? '').toString(),
            from: source?.from,
            to: source?.to,
          }))
          .filter((source) => source.sourceKey);

        let nextCards =
          syncTargets.length === 0
            ? buildSourceCardsFromNoteRows(normalizedNoteRows)
            : buildSourceCardsFromSyncTargets(
                syncTargets,
                normalizedNoteRows,
                statusMap,
              );
        if (syncTargets.length === 0 && tweetSyncResult.status === 'fulfilled') {
          const legacyCard = buildLegacyCommunityFallbackCard(
            tweetSyncResult.value,
            nextCards,
          );
          if (legacyCard) {
            nextCards = [legacyCard, ...nextCards];
          }
        }
        setSourceCardDetails({});
        setSyncResultViewFocus(null);
        setSourceCards(nextCards);
        setSelectedSourceCardId(nextCards[0]?.cardId ?? null);
      } catch (error) {
        console.error('Monitoring log 조회 실패:', error);
        if (activeRef && !activeRef.current) {
          return;
        }
        setMonitoringResults([]);
        setSourceCardDetails({});
        setSyncResultViewFocus(null);
        setSourceCards([]);
        setSelectedSourceCardId(null);
        setIsLegacyTweetSyncMode(false);
      } finally {
        if (!activeRef || activeRef.current) {
          setIsTimelineLoading(false);
        }
      }
    },
    [tokenAddress],
  );

  useEffect(() => {
    if (isOpen && tokenAddress && !tokenData) {
      fetchAndAddTokenState(tokenAddress);
    }
  }, [isOpen, tokenAddress, tokenData, fetchAndAddTokenState]);

  useEffect(() => {
    if (!isOpen) {
      setMonitoringResults([]);
      setIsTimelineLoading(false);
      setVisibleTimelineDays(TIMELINE_PAGE_DAYS);
      setPendingScrollRowIndex(null);
      setSourceCards([]);
      setSourceCardDetails({});
      setSyncResultViewFocus(null);
      setSelectedSourceCardId(null);
      setIsLegacyTweetSyncMode(false);
      setLoadedNoteContents('');
      timelineRowRefs.current = {};
      return;
    }
    if (!tokenAddress) {
      setMonitoringResults([]);
      setVisibleTimelineDays(TIMELINE_PAGE_DAYS);
      setPendingScrollRowIndex(null);
      setSourceCards([]);
      setSourceCardDetails({});
      setSyncResultViewFocus(null);
      setSelectedSourceCardId(null);
      setIsLegacyTweetSyncMode(false);
      setLoadedNoteContents('');
      timelineRowRefs.current = {};
      return;
    }

    const activeRef = { current: true };
    void loadMonitoringLogs(activeRef);

    return () => {
      activeRef.current = false;
    };
  }, [isOpen, tokenAddress, loadMonitoringLogs]);

  const sortedMonitoringResults = useMemo(() => {
    return monitoringResults
      .map((result, originalIndex) => ({ result, originalIndex }))
      .sort((a, b) => {
        return (
          new Date(b.result.monitoringSince).getTime() -
          new Date(a.result.monitoringSince).getTime()
        );
      });
  }, [monitoringResults]);

  const selectedSourceCard = useMemo(
    () => sourceCards.find((c) => c.cardId === selectedSourceCardId) ?? null,
    [sourceCards, selectedSourceCardId],
  );

  const selectedSocialSourceDetail = useMemo(() => {
    if (!selectedSourceCard) return undefined;
    const t = selectedSourceCard.sourceType?.toUpperCase();
    if (t !== 'X_TWEET' && t !== 'X_ACCOUNT') return undefined;
    return sourceCardDetails[selectedSourceCard.cardId];
  }, [selectedSourceCard, sourceCardDetails]);

  const accountPanelHasPinnedTweet = useMemo(() => {
    if (selectedSourceCard?.sourceType?.toUpperCase() !== 'X_ACCOUNT') return false;
    const id = (selectedSourceCard.accountPinnedTweetId ?? '').toString().trim();
    if (id.length > 0) return true;
    return Boolean(selectedSocialSourceDetail?.tweet);
  }, [selectedSourceCard, selectedSocialSourceDetail]);

  const handleSyncResultsPatched = useCallback(
    (cardId: string, nextSyncResults: SyncResultRecord[]) => {
      setSourceCards((prev) =>
        prev.map((c) => (c.cardId === cardId ? { ...c, syncResults: nextSyncResults } : c)),
      );
    },
    [],
  );

  const sourceCardDetailsRef = useRef(sourceCardDetails);
  sourceCardDetailsRef.current = sourceCardDetails;

  useEffect(() => {
    if (!isOpen || sourceCards.length === 0) {
      return;
    }

    const currentDetails = sourceCardDetailsRef.current;
    const targetCards = sourceCards.filter((card) => {
      const sourceType = card.sourceType?.toUpperCase();
      const requiresDetail = sourceType === 'X_TWEET' || sourceType === 'X_ACCOUNT';
      return requiresDetail && !currentDetails[card.cardId];
    });

    if (targetCards.length === 0) {
      return;
    }

    const activeRef = { current: true };

    setSourceCardDetails((prev) => {
      const next = { ...prev };
      targetCards.forEach((card) => {
        const sourceType = card.sourceType?.toUpperCase();
        next[card.cardId] = {
          isLoading: true,
          tweet: null,
          profile: null,
          mode: sourceType === 'X_TWEET' ? 'tweet' : 'account',
        };
      });
      return next;
    });

    const loadCardDetail = async (card: SocialSourceCardModel) => {
      const sourceType = card.sourceType?.toUpperCase();
      const setResult = (detail: Omit<SourceCardDetail, 'isLoading'>) => {
        if (!activeRef.current) return;
        setSourceCardDetails((prev) => ({
          ...prev,
          [card.cardId]: { ...detail, isLoading: false },
        }));
      };

      try {
        if (sourceType === 'X_TWEET') {
          const tweet = await withTimeout(api.getTweetById(card.sourceKey), 10000).catch(() => null);
          setResult({
            tweet: tweet ? mapApiTweetToCardTweet(tweet) : null,
            profile: null,
            mode: 'tweet',
            message: tweet ? undefined : '트윗 정보를 찾지 못했습니다.',
          });
          return;
        }

        const handle = card.sourceKey.replace(/^@+/, '').trim();

        const [profileResult, pinnedTweetIdResult] = await Promise.allSettled([
          withTimeout(api.getAuthorDetailByUsername(handle), 10000),
          card.accountPinnedTweetId
            ? Promise.resolve(card.accountPinnedTweetId)
            : withTimeout(api.getPinnedTweetId(handle), 10000),
        ]);

        if (!activeRef.current) return;

        const profile =
          profileResult.status === 'fulfilled' ? profileResult.value : null;
        const pinnedTweetId =
          pinnedTweetIdResult.status === 'fulfilled'
            ? (pinnedTweetIdResult.value ?? '').trim() || null
            : null;

        let pinnedTweet: TweetCardTweet | null = null;
        if (pinnedTweetId) {
          const rawTweet = await withTimeout(api.getTweetById(pinnedTweetId), 10000).catch(
            () => null,
          );
          if (rawTweet) {
            pinnedTweet = mapApiTweetToCardTweet(rawTweet);
          }
        }

        setResult({
          tweet: pinnedTweet,
          profile,
          mode: 'account',
          message: pinnedTweet
            ? `Pinned tweet`
            : pinnedTweetId
              ? `Pinned tweet ${pinnedTweetId} 조회 실패`
              : profile
                ? 'Pinned tweet 없음'
                : 'Profile / Pinned tweet 없음',
        });
      } catch (error) {
        setResult({
          tweet: null,
          profile: null,
          mode: sourceType === 'X_TWEET' ? 'tweet' : 'account',
          message: '상세 정보를 불러오지 못했습니다.',
        });
      }
    };

    targetCards.forEach((card) => void loadCardDetail(card));

    return () => {
      activeRef.current = false;
    };
  }, [isOpen, sourceCards]);

  const visibleMonitoringResults = useMemo(() => {
    if (sortedMonitoringResults.length === 0) {
      return [];
    }

    const latestTimestamp = new Date(sortedMonitoringResults[0].result.monitoringSince).getTime();
    if (Number.isNaN(latestTimestamp)) {
      return sortedMonitoringResults;
    }

    const visibleThreshold = latestTimestamp - visibleTimelineDays * ONE_DAY_MS;
    return sortedMonitoringResults.filter((row) => {
      const resultTimestamp = new Date(row.result.monitoringSince).getTime();
      if (Number.isNaN(resultTimestamp)) {
        return true;
      }
      return resultTimestamp >= visibleThreshold;
    });
  }, [sortedMonitoringResults, visibleTimelineDays]);

  const overviewMemoRows = useMemo(() => {
    const rows: {
      cardId: string;
      resultIndex: number;
      sourceType: string;
      sourceKey: string;
      monitoringUntil: Date;
      note: string;
    }[] = [];

    for (const card of sourceCards) {
      if (!card.syncResults) continue;
      card.syncResults.forEach((r, resultIndex) => {
        const note = ((r.note ?? '') as string).trim();
        if (!note) return;
        const until = r.monitoring_until ?? r.monitoringUntil;
        rows.push({
          cardId: card.cardId,
          resultIndex,
          sourceType: card.sourceType,
          sourceKey: card.sourceKey,
          monitoringUntil: until ? new Date(until as string) : new Date(0),
          note,
        });
      });
    }

    rows.sort((a, b) => b.monitoringUntil.getTime() - a.monitoringUntil.getTime());
    return rows;
  }, [sourceCards]);

  const handleOverviewRowNavigate = useCallback(
    (row: { cardId: string; resultIndex: number }) => {
      syncResultFocusSeqRef.current += 1;
      setSelectedSourceCardId(row.cardId);
      setSyncResultViewFocus({
        cardId: row.cardId,
        resultIndex: row.resultIndex,
        requestId: syncResultFocusSeqRef.current,
      });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const escaped = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(row.cardId) : row.cardId.replace(/"/g, '\\"');
          const el = document.querySelector(`[data-social-source-card-id="${escaped}"]`);
          el?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
        });
      });
    },
    [],
  );

  const hasMoreTimelineResults = visibleMonitoringResults.length < sortedMonitoringResults.length;

  const handleTimelineScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    if (isTimelineLoading || !hasMoreTimelineResults) {
      return;
    }

    const target = event.currentTarget;
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceFromBottom <= 80) {
      setVisibleTimelineDays((prev) => prev + TIMELINE_PAGE_DAYS);
    }
  }, [hasMoreTimelineResults, isTimelineLoading]);

  const scrollTimelineToRowFast = useCallback((targetEl: HTMLElement) => {
    const container = timelineSectionRef.current;
    if (!container) {
      targetEl.scrollIntoView({ behavior: 'auto', block: 'start' });
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const startScrollTop = container.scrollTop;
    const targetScrollTop = startScrollTop + (targetRect.top - containerRect.top);
    const distance = targetScrollTop - startScrollTop;

    if (Math.abs(distance) < 2) {
      return;
    }

    const durationMs = 180;
    const startTs = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTs;
      const progress = Math.min(1, elapsed / durationMs);
      const eased = easeOutCubic(progress);
      container.scrollTop = startScrollTop + distance * eased;

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    if (isTimelineLoading || !hasMoreTimelineResults) {
      return;
    }

    const timelineSection = timelineSectionRef.current;
    if (!timelineSection) {
      return;
    }

    // If initial rows do not create a scrollbar, auto-expand by next 2-day chunk.
    if (timelineSection.scrollHeight <= timelineSection.clientHeight + 1) {
      setVisibleTimelineDays((prev) => prev + TIMELINE_PAGE_DAYS);
    }
  }, [hasMoreTimelineResults, isTimelineLoading, visibleMonitoringResults.length]);

  useEffect(() => {
    if (pendingScrollRowIndex === null) {
      return;
    }

    const targetEl = timelineRowRefs.current[pendingScrollRowIndex];
    if (targetEl) {
      scrollTimelineToRowFast(targetEl);
      setPendingScrollRowIndex(null);
      return;
    }

    if (hasMoreTimelineResults) {
      setVisibleTimelineDays((prev) => prev + TIMELINE_PAGE_DAYS);
      return;
    }

    setPendingScrollRowIndex(null);
  }, [hasMoreTimelineResults, pendingScrollRowIndex, scrollTimelineToRowFast, visibleMonitoringResults.length]);

  const handleToggleSyncTargetEnabled = useCallback(
    async (event: React.MouseEvent, card: SocialSourceCardModel) => {
      event.stopPropagation();
      event.preventDefault();
      const targetId = card.syncTargetId;
      if (!targetId) return;
      const isEnabled = card.enabled !== false;
      const nextEnabled = !isEnabled;
      const actionWord = nextEnabled ? 'enable' : 'disable';
      if (
        !window.confirm(`정말 해당 싱크를 ${actionWord} 하시겠습니까?`)
      ) {
        return;
      }
      setSyncTargetToggleBusyId(targetId);
      try {
        await feManagementSystemApi.updateSyncTargetEnabled(targetId, nextEnabled);
        setSourceCards((prev) =>
          prev.map((c) =>
            c.cardId === card.cardId ? { ...c, enabled: nextEnabled } : c,
          ),
        );
        showToaster(
          nextEnabled ? 'Sync target enabled' : 'Sync target disabled',
          'success',
        );
      } catch (error: unknown) {
        console.error('Sync target enabled 토글 실패:', error);
        const msg =
          error && typeof error === 'object' && 'response' in error
            ? String((error as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
            : '';
        showToaster(msg || 'Sync target 상태 변경에 실패했습니다.', 'error');
      } finally {
        setSyncTargetToggleBusyId(null);
      }
    },
    [showToaster],
  );

  const handleAddSourceButtonClick = useCallback(() => {
    if (isTimelineLoading) {
      setIsAddSourceOpen((prev) => !prev);
      return;
    }
    const noSources = sourceCards.length === 0;
    const noteStillEmpty = !loadedNoteContents.trim();
    if (noSources && noteStillEmpty && tokenAddress) {
      openCreateNoteModal({
        tokenSymbol: tokenData?.tokenInfo?.tokenSymbol ?? selectedToken?.symbol ?? 'Unknown',
        tokenAddress,
        tokenName: tokenData?.tokenInfo?.tokenName,
        tokenTwitter: tokenData?.tokenInfo?.twitter,
        tokenWebsite: tokenData?.tokenInfo?.website,
        tokenCategory: tokenData?.feTokenState?.category,
        onSuccess: () => {
          void loadMonitoringLogs();
        },
      });
      return;
    }
    setIsAddSourceOpen((prev) => !prev);
  }, [
    isTimelineLoading,
    sourceCards.length,
    loadedNoteContents,
    tokenAddress,
    tokenData,
    selectedToken?.symbol,
    openCreateNoteModal,
    loadMonitoringLogs,
  ]);

  const handleRefreshSocialSources = useCallback(() => {
    if (isTimelineLoading) {
      return;
    }
    const activeRef = { current: true };
    void loadMonitoringLogs(activeRef);
  }, [isTimelineLoading, loadMonitoringLogs]);

  const handleCreateSyncTarget = useCallback(async () => {
    if (!tokenAddress || !newSourceKey.trim() || isCreatingSource) return;
    const tokenSymbolForApi =
      (tokenData?.tokenInfo?.tokenSymbol ?? selectedToken?.symbol ?? '').trim() || 'UNKNOWN';
    setIsCreatingSource(true);
    try {
      await feManagementSystemApi.createSyncTarget(
        tokenAddress,
        newSourceType,
        newSourceKey.trim(),
        tokenSymbolForApi,
      );
      showToaster(`Sync target created: ${newSourceType} / ${newSourceKey.trim()}`, 'success');
      setIsAddSourceOpen(false);
      setNewSourceKey('');
      setNewSourceType('X_TWEET');
      const activeRef = { current: true };
      void loadMonitoringLogs(activeRef);
    } catch (error: any) {
      console.error('Sync target 생성 실패:', error);
      const msg = error?.response?.data?.message ?? error?.message ?? 'Failed to create sync target';
      showToaster(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error');
    } finally {
      setIsCreatingSource(false);
    }
  }, [
    tokenAddress,
    newSourceType,
    newSourceKey,
    isCreatingSource,
    showToaster,
    loadMonitoringLogs,
    tokenData?.tokenInfo?.tokenSymbol,
    selectedToken?.symbol,
  ]);

  useEscapeKeyClose(isOpen, onClose);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={handleBackdropClick}>
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />

      <div
        className="relative h-full bg-gray-900 border-l border-gray-700 shadow-2xl flex flex-col"
        style={{
          width: `${SOCIAL_MODAL_WIDTH}px`,
          boxShadow: '-16px 0px 50px 20px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <TokenDetailHeader
          tokenAddress={tokenData?.tokenAddress || ''}
          tokenData={tokenData}
          onClose={onClose}
        />

        <div className="flex-1 min-h-0 p-4">
          <div className="h-full grid grid-cols-[520px_1fr] gap-4">
            <div className="h-full flex flex-col gap-4">
              <section className="rounded-lg border border-gray-700 bg-gray-800 p-4 max-h-[500px] h-full shrink-0 overflow-y-auto">
                <h3 className="text-sm font-semibold text-gray-200 mb-2">Overview</h3>
                {overviewMemoRows.length === 0 ? (
                  <div className="text-sm text-gray-400">메모가 있는 Sync Result가 없습니다.</div>
                ) : (
                  <div className="max-h-[420px] overflow-y-auto pr-1 space-y-2">
                    {overviewMemoRows.map((row, index) => (
                      <div
                        key={`overview-${row.cardId}-${row.resultIndex}-${row.monitoringUntil.getTime()}-${index}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleOverviewRowNavigate(row)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleOverviewRowNavigate(row);
                          }
                        }}
                        className="cursor-pointer rounded px-2 py-2 text-left text-xs leading-relaxed outline-none transition-colors hover:bg-gray-700/40 focus-visible:ring-2 focus-visible:ring-blue-500/50"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <SocialSourceTypeChip type={row.sourceType} />
                          <span className="text-[11px] text-gray-400 truncate max-w-[140px]">{row.sourceKey}</span>
                          <span className="text-[11px] text-gray-500 ml-auto shrink-0">
                            {formatTimestampToLocaleString(row.monitoringUntil)}
                          </span>
                        </div>
                        <div className="text-gray-200 whitespace-pre-wrap break-words">{row.note}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <div className="h-[320px] shrink-0 overflow-hidden">
                {tokenAddress ? (
                  <GmgnChart
                    chain="sol"
                    tokenAddress={tokenAddress}
                    tokenSymbol={tokenData?.tokenInfo?.tokenSymbol}
                    theme="dark"
                    interval="15"
                    width="100%"
                    height="100%"
                    className="w-full h-full"
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-gray-500">
                    No token selected
                  </div>
                )}
              </div>

              {selectedSourceCard?.sourceType?.toUpperCase() === 'X_TWEET' && (
                <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800/80 p-3">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    모니터링 트윗
                  </h3>
                  {!selectedSocialSourceDetail || selectedSocialSourceDetail.isLoading ? (
                    <div className="text-sm text-gray-500">트윗 정보를 불러오는 중…</div>
                  ) : selectedSocialSourceDetail.tweet ? (
                    <TweetCard tweet={selectedSocialSourceDetail.tweet} />
                  ) : (
                    <div className="text-sm text-gray-500">
                      {selectedSocialSourceDetail.message ?? '트윗 정보를 표시할 수 없습니다.'}
                    </div>
                  )}
                </div>
              )}

              {selectedSourceCard?.sourceType?.toUpperCase() === 'X_ACCOUNT' &&
                selectedSocialSourceDetail &&
                !selectedSocialSourceDetail.isLoading &&
                selectedSocialSourceDetail.tweet && (
                  <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800/80 p-3">
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Pinned Tweet
                    </h3>
                    <TweetCard tweet={selectedSocialSourceDetail.tweet} />
                  </div>
                )}
            </div>

            <section
              ref={timelineSectionRef}
              className="rounded-lg border border-gray-700 bg-gray-800 p-4 min-h-0 flex flex-col"
              onScroll={handleTimelineScroll}
            >
              <div className="mb-3 rounded-md border border-gray-700 bg-gray-900/40 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-300">
                    Social Sources
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500">{sourceCards.length} sources</span>
                    {!isLegacyTweetSyncMode && (
                      <>
                        <button
                          type="button"
                          onClick={handleRefreshSocialSources}
                          disabled={isTimelineLoading}
                          className="rounded border border-gray-600 px-2 py-0.5 text-[11px] font-medium text-gray-200 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isTimelineLoading ? 'Refreshing...' : 'Refresh'}
                        </button>
                        <button
                          type="button"
                          onClick={handleAddSourceButtonClick}
                          className="rounded border border-blue-500/60 bg-blue-600/20 px-2 py-0.5 text-[11px] font-medium text-blue-300 hover:bg-blue-600/40 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          + Add Source
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {!isLegacyTweetSyncMode && isAddSourceOpen && (
                  <div className="mb-2 rounded-md border border-blue-500/40 bg-gray-900/60 p-3">
                    <div className="flex items-end gap-2">
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-gray-400">Type</label>
                        <select
                          value={newSourceType}
                          onChange={(e) => setNewSourceType(e.target.value as 'X_TWEET' | 'X_ACCOUNT' | 'X_COMMUNITY')}
                          disabled={isCreatingSource}
                          className="rounded border border-gray-600 bg-gray-700 px-2 py-1 text-xs text-white focus:border-blue-500 focus:outline-none disabled:opacity-50"
                        >
                          <option value="X_TWEET">X_TWEET</option>
                          <option value="X_ACCOUNT">X_ACCOUNT</option>
                          <option value="X_COMMUNITY">X_COMMUNITY</option>
                        </select>
                      </div>
                      <div className="flex flex-1 flex-col gap-1">
                        <label className="text-[10px] text-gray-400">Source Key</label>
                        <input
                          type="text"
                          value={newSourceKey}
                          onChange={(e) => setNewSourceKey(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newSourceKey.trim()) {
                              void handleCreateSyncTarget();
                            }
                          }}
                          placeholder="tweetId / handle / communityId"
                          disabled={isCreatingSource}
                          className="rounded border border-gray-600 bg-gray-700 px-2 py-1 text-xs text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none disabled:opacity-50"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleCreateSyncTarget}
                        disabled={isCreatingSource || !newSourceKey.trim()}
                        className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isCreatingSource ? 'Creating...' : 'Sync'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddSourceOpen(false);
                          setNewSourceKey('');
                          setNewSourceType('X_TWEET');
                        }}
                        className="rounded border border-gray-600 px-2 py-1 text-xs text-gray-400 hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {isTimelineLoading ? (
                  <div className="text-xs text-gray-500">Loading sources...</div>
                ) : sourceCards.length === 0 ? (
                  <div className="text-xs text-gray-500">
                    동기화 대상이 없습니다.
                  </div>
                ) : (
                  <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1">
                    {sourceCards.map((card) => {
                      const isSelected = card.cardId === selectedSourceCardId;
                      const xUrl = getXWebUrlForSocialSource(card.sourceType, card.sourceKey);
                      return (
                        <div
                          key={card.cardId}
                          data-social-source-card-id={card.cardId}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedSourceCardId(card.cardId)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedSourceCardId(card.cardId);
                            }
                          }}
                          className={`w-[420px] shrink-0 cursor-pointer rounded-md border bg-gray-900/60 p-3 text-left outline-none transition-colors hover:bg-gray-900/80 focus-visible:ring-2 focus-visible:ring-blue-500/50 ${
                            isSelected
                              ? 'border-blue-500 ring-1 ring-blue-500/35'
                              : 'border-gray-700'
                          }`}
                        >
                          <div className="mb-1 flex items-start justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-1">
                              <SocialSourceTypeChip type={card.sourceType} />
                              {card.origin === 'note' && (
                                <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-200/90">
                                  Note only
                                </span>
                              )}
                              {card.origin === 'sync' && (
                                <span
                                  className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${getSyncEnabledChipClassName(
                                    card.enabled !== false,
                                  )}`}
                                >
                                  {card.enabled !== false ? 'Enabled' : 'Disabled'}
                                </span>
                              )}
                            <span
                              className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${getStatusChipClassName(
                                card.trackerStatus,
                              )}`}
                            >
                              {card.trackerStatus ?? 'UNKNOWN'}
                            </span>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              {card.origin === 'sync' && card.syncTargetId ? (
                                <button
                                  type="button"
                                  disabled={syncTargetToggleBusyId === card.syncTargetId}
                                  onClick={(e) => void handleToggleSyncTargetEnabled(e, card)}
                                  className={`rounded border px-2 py-0.5 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                    card.enabled !== false
                                      ? 'border-rose-500/50 text-rose-300 hover:bg-rose-500/15'
                                      : 'border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/15'
                                  }`}
                                >
                                  {syncTargetToggleBusyId === card.syncTargetId
                                    ? '...'
                                    : card.enabled !== false
                                      ? 'Disable'
                                      : 'Enable'}
                                </button>
                              ) : null}
                              {xUrl ? (
                                <a
                                  href={xUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(event) => event.stopPropagation()}
                                  onKeyDown={(event) => event.stopPropagation()}
                                  className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-sky-400 hover:text-sky-300 hover:underline"
                                >
                                  Open on X
                                  <span className="text-[10px] opacity-80" aria-hidden>
                                    ↗
                                  </span>
                                </a>
                              ) : null}
                            </div>
                          </div>
                          <div className="text-[11px] leading-relaxed text-gray-200">
                            <div className="flex items-center gap-2">
                              <div className="truncate text-[14px] font-medium text-gray-100">
                                {card.sourceKey}
                              </div>
                              <CopyButton text={card.sourceKey} title="소스 키 복사" />
                            </div>
                            <div className="truncate text-[12px] text-gray-300">
                              {card.from ? formatTimestampToLocaleString(card.from) : '-'} ~{' '}
                              {card.to ? formatTimestampToLocaleString(card.to) : '-'}
                            </div>
                            {card.sourceType?.toUpperCase() === 'X_TWEET' && (
                              <div className="mt-2 rounded border border-gray-700 bg-gray-900/60 p-2">
                                {sourceCardDetails[card.cardId]?.isLoading ? (
                                  <div className="text-[11px] text-gray-500">loading tweet...</div>
                                ) : sourceCardDetails[card.cardId]?.tweet ? (
                                  <div className="space-y-1">
                                    <div className="text-[11px] text-gray-300">
                                      @{getDisplayHandle(sourceCardDetails[card.cardId]?.tweet?.author?.userName)}{' '}
                                      · {formatTimestampToLocaleString(sourceCardDetails[card.cardId]?.tweet?.createdAt ?? '')}
                                    </div>
                                    <div className="line-clamp-3 text-[12px] text-gray-100">
                                      {sourceCardDetails[card.cardId]?.tweet?.text}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-[11px] text-gray-500">
                                    {sourceCardDetails[card.cardId]?.message ?? 'no detail'}
                                  </div>
                                )}
                              </div>
                            )}
                            {card.sourceType?.toUpperCase() === 'X_ACCOUNT' && (
                              <div className="mt-2 rounded border border-gray-700 bg-gray-900/60 p-2 space-y-2">
                                {sourceCardDetails[card.cardId]?.isLoading ? (
                                  <div className="text-[11px] text-gray-500">loading profile...</div>
                                ) : (
                                  <>
                                    {sourceCardDetails[card.cardId]?.profile && (() => {
                                      const prof = sourceCardDetails[card.cardId]!.profile!;
                                      return (
                                        <div className="flex items-start gap-2">
                                          {prof.profilePicture && (
                                            <img
                                              src={prof.profilePicture}
                                              alt=""
                                              className="h-8 w-8 rounded-full shrink-0 bg-gray-700"
                                            />
                                          )}
                                          <div className="min-w-0 flex-1">
                                            <div className="text-[12px] font-medium text-gray-100 truncate">
                                              {prof.name ?? prof.displayName ?? getDisplayHandle(prof.userName)}
                                            </div>
                                            <div className="text-[11px] text-gray-400">
                                              @{getDisplayHandle(prof.userName)} · {prof.followers?.toLocaleString() ?? '-'} followers
                                            </div>
                                            {prof.description && (
                                              <div className="mt-1 line-clamp-2 text-[11px] text-gray-300">
                                                {prof.description}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                    {sourceCardDetails[card.cardId]?.tweet ? (
                                      <div className="rounded border border-gray-600/50 bg-gray-800/40 p-1.5 space-y-0.5">
                                        <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Pinned Tweet</div>
                                        <div className="line-clamp-3 text-[12px] text-gray-100">
                                          {sourceCardDetails[card.cardId]?.tweet?.text}
                                        </div>
                                      </div>
                                    ) : !sourceCardDetails[card.cardId]?.profile ? (
                                      <div className="text-[11px] text-gray-500">
                                        {sourceCardDetails[card.cardId]?.message ?? 'no detail'}
                                      </div>
                                    ) : null}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="rounded-md border border-gray-700 overflow-hidden flex-1 min-h-0 overflow-y-auto">
                {isTimelineLoading ? (
                  <div className="p-4 text-sm text-gray-400">Loading monitoring logs...</div>
                ) : (
                  <SocialSourceDetailPanel
                    tokenAddress={tokenAddress}
                    selectedSourceCard={selectedSourceCard}
                    accountHasPinnedTweet={accountPanelHasPinnedTweet}
                    syncResultViewFocus={syncResultViewFocus}
                    formatTimestampToLocaleString={formatTimestampToLocaleString}
                    getStatusChipClassName={getStatusChipClassName}
                    onSyncResultsPatched={handleSyncResultsPatched}
                  />
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SocialDetailModal;
